import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@core/services/auth.service';
import { StudentRequestService } from '@core/services/student-request.service';
import { ClassService } from '@core/services/class.service';
import { forkJoin, take } from 'rxjs';
import { IRatingPeriod, ISchoolYear } from '@core/interfaces/iratingperiod';
import { StudentRequestReason } from '@core/enums/student-request-reason';
import { EnrollmentTypeIdEnum } from '@core/enums/enrollment-type-id.enum';
import { GlobalRatingPeriod } from '@core/enums/global-rating-period-id.enum';
import { IClass, IClassroom } from '@core/interfaces/iclasses';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  IStudentRequest,
  IStudentRequestBatch,
} from '@core/interfaces/istudent-request';
import { IStudentSearchResponseDto } from '@core/interfaces/istudent';
import { ToastService } from '@core/services/toast.service';
import { CustomValidators } from '@core/validators/custom.validator';
import { ClassroomService } from '@core/services/classroom.service';
import { AgencyService } from '@core/services/agency.service';
import { Modal } from '@core/enums/modal';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { LookupService } from '@core/services/lookup.service';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { StateService } from '@core/services/state.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-batch-re-enroll-child',
    templateUrl: './batch-re-enroll-child.component.html',
    styleUrls: ['./batch-re-enroll-child.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BatchReEnrollChildComponent implements OnInit {
  user = this.authService.getCurrentUser();
  ratingOptions: IRatingPeriod[] = [];
  submitBtnText = '';
  isAdmin: boolean = false;
  reEnrollmentForm: FormGroup | any;
  existingClasses: IClass[] = [];
  displayResult = false;
  isProcessing = false;
  students?: IStudentSearchResponseDto[];
  rpStartDate!: number;
  rpEndDate!: number;
  agencyId?: number;
  isSoftImplementation: boolean = false;
  ratingViewVersion: number = 0;
  requireAgeGrade: boolean = false;
  currentSchoolYear?: ISchoolYear;
  selectedRatingPeriod?: IRatingPeriod;
  selectedSchoolYearId: number | null = null;
  is2025StateConfig = false;
  isUserState2025Enforced = false;

  get ratingPeriodId() {
    return this.reEnrollmentForm.get('ratingPeriodId');
  }
  get classExitDate() {
    return this.reEnrollmentForm.get('classExitDate');
  }
  get classEnrollmentDate() {
     return this.reEnrollmentForm.get('classEnrollmentDate');
  }
  get ageGradeId() {
     return this.reEnrollmentForm.get('ageGradeId');
  }

  constructor(
    private authService: AuthService,
    private permissionService: PermissionService,
    private studentRequestService: StudentRequestService,
    private classService: ClassService,
    private classroomService: ClassroomService,
    private toastService: ToastService,
    private agencyService: AgencyService,
    private lookupService: LookupService,
    private fb: FormBuilder,
    private stateService: StateService,
    public dialogRef: MatDialogRef<BatchReEnrollChildComponent>,
    public dialog: MatDialog,
    private translocoService: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.initializeForms();
    this.getCurrentSchoolYear()
    this.agencyId = this.data.agencyId;

    this.stateService.isUserState2025Enforced().pipe(take(1)).subscribe((enforced) => (this.isUserState2025Enforced = enforced));

    this.isAdmin = this.permissionService.checkPermission(
      Permission.AdminEnrollmentByPass
    );

    this.submitBtnText = this.isAdmin
      ? this.translocoService.translate('user.manageEnrollment.batchReEnroll.submit')
      : this.translocoService.translate('user.manageEnrollment.batchReEnroll.submitForApproval');

    this.classService
      .getClassesByClassroom(this.data.classroomId)
      .pipe(take(1))
      .subscribe((result) => {
        const ratingPeriods = result
          .flatMap((obj) => obj.ratingPeriod)
          .filter((rp) => rp.agencyId === this.agencyId);
        const currentRatingPeriod = ratingPeriods.find(
          (r: any) => r.id == this.data.ratingPeriodId
        );

        if (currentRatingPeriod) {
          this.ratingOptions = ratingPeriods;
          this.students = this.data.students;
          this.existingClasses = result;
          this.displayResult = true;
        }
      });

      const isSoftImplementation = {
        agency: false,
        classroom: false
      }
      
      const agency$ = this.agencyService.getAgency(this.agencyId).pipe(take(1));
      const classroom$ = this.classroomService.getClassroomById(this.data.classroomId).pipe(take(1));
      
      forkJoin({
            agency: agency$,
            classroom: classroom$
          }).subscribe(({ agency, classroom }) => {
            isSoftImplementation.agency = agency.isSoftImplementation;
            isSoftImplementation.classroom = classroom.some((c: IClassroom) => c.isSoftImplementation) ?? this.data.isSoftImplementation;

            this.isSoftImplementation = isSoftImplementation.agency || isSoftImplementation.classroom || this.data.isSoftImplementation;
            this.ratingViewVersion = agency.state?.ratingViewVersionId || 0;
          });
  }

  getCurrentSchoolYear() {
    this.lookupService.getCurrentSchoolYear().pipe(
      take(1)
    ).subscribe((sy) => {
      this.currentSchoolYear = sy;
    });
  }

  isCurrentDateBelowMinStartDate (minStartDate: string): boolean {
    const currentDate = new Date();
    const dateWithoutTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    return dateWithoutTime < new Date(minStartDate);
  };

  filterRatingOptions(ratingPeriods: IRatingPeriod[], currentRatingPeriod: IRatingPeriod): IRatingPeriod[] {
    let ratingOptions = ratingPeriods.filter(
      (r: IRatingPeriod) =>
        new Date(r.startDate) > new Date(currentRatingPeriod.startDate) &&
        r.id != this.data.ratingPeriodId
        &&
        !this.isCurrentDateBelowMinStartDate(r.minDate.toString())
    );
    return ratingOptions;
  }

  initializeForms(): void {
    this.reEnrollmentForm = this.fb.group({
      ratingPeriodId: [null, Validators.required],
      classEnrollmentDate: [null, [Validators.required]],
      classExitDate: [null, [Validators.required]],
      ageGradeId: [null],
    }, {validator: [CustomValidators.dateValidator('classExitDate', 'classEnrollmentDate')]
    });
  }

  handleDate(event: any, controlName: string): void {
    if (event === '' || !event) {
      this.reEnrollmentForm.get(controlName).setValue(null);
    } else {
     this.reEnrollmentForm.get(controlName).setValue(event);
    }
    this.checkEnrollmentDate();
  }

  checkEnrollmentDate(): void {
    if (this.classEnrollmentDate.value && this.ratingPeriodId.value) {
      const checkDate = new Date(this.classEnrollmentDate.value).getTime();
      if (checkDate < this.rpStartDate || checkDate > this.rpEndDate) {
        this.classEnrollmentDate.setErrors({dateNotWithinRatingPeriod: true});
      } else {
        this.classEnrollmentDate.setErrors(null);
      }
    }
  }

  handleRatingPeriod(event: any): void {
    event = this.ratingOptions.find(rp => rp.id == event.value);
    this.selectedRatingPeriod = event;    
    this.ratingPeriodId.setValue(event.id);
    this.rpStartDate = new Date(event.minDate).getTime();
    this.rpEndDate = new Date(event.maxDate).getTime();
    this.checkEnrollmentDate();
    
    this.selectedSchoolYearId = event.schoolYearId ?? null;
    this.is2025StateConfig = event.schoolYear.enforceStateConfig && this.ratingViewVersion == RatingViewVersionEnum.VERSION_2025;
    this.requireAgeGrade = this.is2025StateConfig || this.isSoftImplementation;

    this.ageGradeId.setValidators(this.requireAgeGrade ? [Validators.required] : []);
    this.ageGradeId.updateValueAndValidity();
  }

  handleAgeGrade(ageGrade: any): void {
    this.ageGradeId.setValue(ageGrade?.id);
  }

  submit(): void {
    if (this.reEnrollmentForm.invalid) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.common.requiredFieldsError')
      );
      return;
    }

    const reEnrollClass = this.existingClasses.filter(
      (ec) => ec.ratingPeriodId == this.ratingPeriodId.value
    )[0];

    const payload: IStudentRequestBatch = {
      studentRequestDtos: [],
      requestReasonId: StudentRequestReason.ReEnrollStudent,
      isBatch: true,
    };

    this.isProcessing = true;
    const successMessage = this.isAdmin
      ? this.translocoService.translate('user.manageEnrollment.batchReEnroll.reEnrollSuccess')
      : this.translocoService.translate('user.manageEnrollment.batchReEnroll.reEnrollRequestSent');

    if (this.students?.length) {
      if (this.students.some(student => student.classroomId == this.data.classroomId && student.ratingPeriodId == this.ratingPeriodId.value)) {
        let modal = {
          data: {
            modalInfo: {
              message: this.translocoService.translate(
                'user.manageEnrollment.batchReEnroll.alreadyEnrolledError'
              ),
              primaryBtnText: Modal.OK
            }
          }
        };
        this.dialog.open(ConfirmationModalComponent, { data: modal });
        this.isProcessing = false;
        return;
      }

      if (this.students.some(student => this.ratingPeriodId.value < student.ratingPeriodId)) {
        this.toastService.error(
          this.translocoService.translate('user.manageEnrollment.batchReEnroll.ratingPeriodOrderError')
        );
        this.isProcessing = false;
        return;
      }

      const hasFieldStudyStudent = this.students?.some(student => student.enrollmentTypeId === EnrollmentTypeIdEnum.FieldStudy);
      const isTargetFall2025 = this.selectedRatingPeriod?.globalRatingPeriod?.id === GlobalRatingPeriod.FALL_2025;
      if (hasFieldStudyStudent && !isTargetFall2025) {
        this.toastService.error(
          this.translocoService.translate('user.manageEnrollment.batchReEnroll.fieldStudyRatingPeriodError')
        );
        this.isProcessing = false;
        return;
      }

      if (this.isUserState2025Enforced && !this.is2025StateConfig) {
        this.toastService.error(this.translocoService.translate('shared.ratingPeriods.ratingViewRejected'));
        this.isProcessing = false;
        return;
      }

      const studentRequest: IStudentRequest[] = this.students.map(
        (student: IStudentSearchResponseDto) => ({
          fromClassId: this.data.fromClassId,
          toClassId: reEnrollClass.id,
          studentId: student.id,
          requestReasonId: StudentRequestReason.ReEnrollStudent,
          siteId: student.siteId,
          studentName: `${student.firstName} ${student.lastName}`,
          classEnrollmentDate: this.classEnrollmentDate.value,
          classExitDate: this.classExitDate.value,
          studentClassEnrollmentId: student.sceId,
          ageGradeId: this.requireAgeGrade ? this.ageGradeId.value : null
        })
      );
      payload.studentRequestDtos = studentRequest;
      payload.siteId = studentRequest[0].siteId;


    this.studentRequestService.checkDuplicateBatch(payload)
    .pipe(take(1))
    .subscribe((duplicates) => {
      if(duplicates && duplicates.length > 0) {
        this.dialogRef.close({ success: false, duplicates: duplicates });
      }
      else {
          this.studentRequestService
          .processBatchRequest(payload, successMessage)
          .pipe(take(1))
          .subscribe((_) => {
            this.isProcessing = false;
            this.dialogRef.close({ success: true });
          });
        }
      });
    }
  }
}
